import styled from '@emotion/styled/macro'
import {
  DayPickerStrings,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { Field, FieldProps } from 'formik'
import { FormikDropdown } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { ParticipantFerryInput, Proving } from 'model'
import {
  DayOfWeek,
  IDropdownOption,
  Label,
  Stack,
} from 'office-ui-fabric-react'
import { IconButton } from 'office-ui-fabric-react/lib/Button'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ParticipantListItem } from 'views/ParticipantViews'
import { useDataQuery } from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

export type ProvingDetailsFerryFormValues = ParticipantFerryInput

export class ProvingDetailsFerryFormSchema {
  constructor(provingDateFrom: string, provingDateTo: string) {
    return Yup.object().shape({
      ferry: Yup.object()
        .shape({
          outward: Yup.string().nullable(true),
          dateFrom: Yup.date()
            .nullable()
            .when('outward', (outward: any, schema: any) =>
              outward ? schema.required('DateFrom is a required field') : schema
            ),
          return: Yup.string().nullable(true),
          dateTo: Yup.date()
            .nullable()
            .when('return', (returnVal: any, schema: any) =>
              returnVal ? schema.required('DateTo is a required field') : schema
            )
            .when('dateFrom', (dateFrom: any, schema: any) => {
              if (dateFrom) {
                return schema.min(dateFrom)
              } else {
                return schema.min(
                  new Date(new Date(provingDateFrom).valueOf() - 86400000)
                )
              }
            }),
        })
        .test(
          'at-least-one-ferrytype',
          'You must provide at least 1 ferry type',
          (value) => !!(value.outward || value.return)
        )
        .test(
          'cant be empty',
          'Date cannot be filled without a ferry',
          (value) =>
            !(
              (!value.outward && value.dateFrom) ||
              (!value.return && value.dateTo)
            )
        ),
    })
  }
}

export const TimePicker = styled(Field)`
  border-style: solid;
  border-color: rgb(138, 136, 134);
  border-width: 1px;
  border-radius: 2px;
  height: 32px;
  line-height: 30px;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  background: rgb(255, 255, 255);
  padding: 0px 24px 0px 8px;
  overflow: hidden;
`

export const ProvingDetailsFerryForm: React.FC<{
  notification?: boolean
  proving: Partial<Proving>
  participant?: Partial<ParticipantListItem>
}> = ({ notification, proving, participant }) => {
  const { t } = useTranslation()
  const [, setSelectedType] = React.useState()
  const { data } = useDataQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
    },
  })

  const ferryConnections =
    data && data.resources && data.resources ? data.resources : []
  const ferryConnectionsOutward = ferryConnections.filter(
    (ferryConnection) => ferryConnection.type === 'outward'
  )
  const ferryConnectionsReturn = ferryConnections.filter(
    (ferryConnection) => ferryConnection.type === 'return'
  )

  const resetOutward = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.outward', null)
    fieldProps.form.setFieldValue('ferry.dateFrom', undefined)
    fieldProps.form.setFieldValue('ferry.timeFrom', null)
  }

  const resetReturn = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.return', null)
    fieldProps.form.setFieldValue('ferry.dateTo', undefined)
    fieldProps.form.setFieldValue('ferry.timeTo', null)
  }

  const setOutward = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.dateFrom', proving.dateFrom)
    fieldProps.form.setFieldValue('ferry.timeFrom', '00:00')
  }

  const setReturn = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.dateTo', proving.dateTo)
    fieldProps.form.setFieldValue('ferry.timeTo', '00:00')
  }

  // const validateDateFrom = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateFrom as any)
  //   } else {
  //     return new Date(proving.dateFrom)
  //   }
  // }

  // const validateDateTo = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateTo as any)
  //   } else {
  //     return new Date(proving.dateTo)
  //   }
  // }

  return (
    <FormStack>
      {notification && (
        <FieldStack>
          <Label>
            {t(
              'Achtung! Es sind mehrere Einträge ausgewählt! Die Werte werden bei allen ausgewählten Einträgen mit den Werten aus der Formularmaske überschrieben!'
            )}
          </Label>
        </FieldStack>
      )}
      <Label>{t('field.ferryConnection.header.label')}</Label>
      {/* <FieldStack>
        <FieldStackItem
          name="ferry.required"
          render={fieldProps => {
            setDisableFields(!fieldProps.field.value)
            return (
              <FormikToggle
                label={t('field.ferryConnection.ferryRequired.label')}
                inlineLabel={true}
                {...fieldProps}
                onChanged={option => {
                  if (!option) {
                    fieldProps.form.setFieldValue('ferry.outward', null)
                    fieldProps.form.setFieldValue('ferry.return', null)
                    fieldProps.form.setFieldValue('ferry.dateTo', null)
                    fieldProps.form.setFieldValue('ferry.dateFrom', null)
                    fieldProps.form.setFieldValue('ferry.timeTo', null)
                    fieldProps.form.setFieldValue('ferry.timeFrom', null)
                  }
                }}
              />
            )
          }}
        />
      </FieldStack> */}
      <>
        <FieldStack horizontal={true}>
          <FieldStackItem
            name="ferry.outward"
            render={(fieldProps: FieldProps<ProvingDetailsFerryFormValues>) => (
              <FormikDropdown
                label={t('field.ferryConnection.outward.label')}
                {...fieldProps}
                options={[
                  { key: '', text: '' },
                  ...ferryConnectionsOutward.map(
                    (ferryConnectionOutward: any) =>
                      ({
                        key: ferryConnectionOutward._id,
                        text: ferryConnectionOutward.title,
                      } as IDropdownOption)
                  ),
                ]}
                onChanged={(option) => {
                  if (option.key === '') {
                    setSelectedType(option.key)
                    resetOutward(fieldProps)
                  } else {
                    setOutward(fieldProps)
                  }
                }}
              />
            )}
          />
          <FieldStackItem
            name="ferry.dateFrom"
            label={t('field.ferryConnection.dateFrom.label')}
            component={FormikGraphQLDatePicker}
            // minDate={validateDateFrom()}
            // maxDate={validateDateTo()}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            initialPickerDate={new Date(proving.dateFrom)}
          />
          <Stack>
            <Label>{t('field.ferryConnection.timeFrom.label')}</Label>
            <Stack.Item>
              <TimePicker component="input" type="time" name="ferry.timeFrom" />
            </Stack.Item>
          </Stack>

          <FieldStackItem
            name="resetFerryFrom"
            render={(fieldProps: FieldProps<ProvingDetailsFerryFormValues>) => (
              <>
                <Label>löschen</Label>
                <IconButton
                  iconProps={{ iconName: 'Clear' }}
                  title="löschen"
                  onClick={() => resetOutward(fieldProps)}
                />
              </>
            )}
          />
        </FieldStack>
        <FieldStack horizontal={true}>
          <FieldStackItem
            name="ferry.return"
            render={(fieldProps: FieldProps<ProvingDetailsFerryFormValues>) => (
              <FormikDropdown
                label={t('field.ferryConnection.return.label')}
                {...fieldProps}
                options={[
                  { key: '', text: '' },
                  ...ferryConnectionsReturn.map(
                    (ferryConnectionReturn: any) =>
                      ({
                        key: ferryConnectionReturn._id,
                        text: ferryConnectionReturn.title,
                      } as IDropdownOption)
                  ),
                ]}
                onChanged={(option) => {
                  if (option.key === '') {
                    setSelectedType(option.key)
                    resetReturn(fieldProps)
                  } else {
                    setReturn(fieldProps)
                  }
                }}
              />
            )}
          />
          <FieldStackItem
            name="ferry.dateTo"
            label={t('field.ferryConnection.dateTo.label')}
            component={FormikGraphQLDatePicker}
            // minDate={validateDateFrom()}
            // maxDate={validateDateTo()}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            initialPickerDate={new Date(proving.dateFrom)}
          />
          <Stack>
            <Label>{t('field.ferryConnection.timeTo.label')}</Label>
            <Stack.Item>
              <TimePicker component="input" type="time" name="ferry.timeTo" />
            </Stack.Item>
          </Stack>
          <FieldStackItem
            name="resetFerryTo"
            render={(fieldProps: FieldProps<ProvingDetailsFerryFormValues>) => (
              <>
                <Label>löschen</Label>
                <IconButton
                  iconProps={{ iconName: 'Clear' }}
                  title="löschen"
                  onClick={() => resetReturn(fieldProps)}
                />
              </>
            )}
          />
        </FieldStack>
      </>
    </FormStack>
  )
}
